import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useUpdatePaymentMethodMutation } from "../../api/subscriptionApi";

interface UpdatePaymentMethodDialogProps {
  open: boolean;
  onClose: () => void;
}

const cardElementOptions = {
  style: {
    base: {
      fontSize: "16px",
      color: "#424770",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#9e2146",
    },
  },
};

const UpdatePaymentMethodDialog: React.FC<UpdatePaymentMethodDialogProps> = ({
  open,
  onClose,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [updatePaymentMethod, { isLoading }] = useUpdatePaymentMethodMutation();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      return;
    }

    setErrorMessage(null);
    setSuccessMessage(null);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setErrorMessage("Card element not found");
      return;
    }

    try {
      // Create payment method with Stripe
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
      });

      if (error) {
        setErrorMessage(error.message || "Failed to create payment method");
        return;
      }

      if (!paymentMethod) {
        setErrorMessage("Failed to create payment method");
        return;
      }

      // Update payment method on backend
      await updatePaymentMethod({
        paymentMethodId: paymentMethod.id,
      }).unwrap();

      setSuccessMessage("Payment method updated successfully!");
      
      // Close dialog after a short delay
      setTimeout(() => {
        onClose();
        setSuccessMessage(null);
      }, 1500);
    } catch (err: any) {
      setErrorMessage(err?.data?.message || "Failed to update payment method");
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setErrorMessage(null);
      setSuccessMessage(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update Payment Method</DialogTitle>
      <DialogContent>
        <Box mt={2} mb={3}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Enter your new card details below. Your previous payment method will be removed.
          </Typography>
        </Box>

        <Box
          sx={{
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            mb: 2,
          }}
        >
          <CardElement options={cardElementOptions} />
        </Box>

        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {successMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!stripe || isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? "Updating..." : "Update Payment Method"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdatePaymentMethodDialog;

